import React from 'react'
import { Clock, CheckCircle, Calendar, Check } from 'lucide-react'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PayableStatusBadgeProps {
  status: 'pending' | 'approved' | 'scheduled' | 'paid' | 'returned'
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * PayableStatusBadge - Displays payable status with icon and color coding
 *
 * Features:
 * - Color-coded badges matching Monumenta design system
 * - Status-specific icons from lucide-react
 * - Accessible with aria-label
 * - Inline display with flex layout
 *
 * Status color mapping:
 * - pending → .status-pending (orange/warning)
 * - approved → .status-active (green/success)
 * - scheduled → .status-info (blue/info)
 * - paid → .status-active (green/success)
 * - returned → .status-inactive (red/error)
 *
 * @param status - The payable status to display
 */
export function PayableStatusBadge({ status }: PayableStatusBadgeProps) {
  const config = {
    pending: {
      label: 'Pending',
      className: 'status-pending',
      icon: Clock
    },
    approved: {
      label: 'Approved',
      className: 'status-active',
      icon: CheckCircle
    },
    scheduled: {
      label: 'Scheduled',
      className: 'status-info',
      icon: Calendar
    },
    paid: {
      label: 'Paid',
      className: 'status-active',
      icon: Check
    },
    returned: {
      label: 'Returned',
      className: 'status-inactive',
      icon: Clock
    }
  }

  const { label, className, icon: Icon } = config[status]

  return (
    <span
      className={`${className} inline-flex items-center gap-1`}
      role="status"
      aria-label={`Payment status: ${label}`}
    >
      <Icon size={12} />
      {label}
    </span>
  )
}

export default PayableStatusBadge
